import { useState } from "react";
import {
  investors as api,
  type Investor,
  type InvestorStage,
  type KycStatus,
} from "../../../api";
import { formatDateTime } from "../format";
import { RISK_LABELS } from "../../portfolio/OpenAccountWizard";
import { INVESTOR_TYPE_LABELS } from "../../../components/ClientTypeIcon";

export function InvestorProfile({
  investor,
  onComplianceChange,
}: {
  investor: Investor;
  onComplianceChange: (investor: Investor) => void;
}) {
  return (
    <>
      <dl className="info-grid">
        <Field label="Type" value={INVESTOR_TYPE_LABELS[investor.type]} />
        {investor.type === "company" && (
          <Field label="Org number" value={investor.org_id} mono />
        )}
        <Field label="Email" value={investor.email} mono />
        <Field label="Phone" value={investor.phone} mono />
        <Field label="Address" value={investor.address} />
        <Field label="City" value={formatCity(investor.zip, investor.city)} />
        <Field label="Added" value={formatDateTime(investor.created_at)} />
        <Field label="Last updated" value={formatDateTime(investor.updated_at)} />
      </dl>

      <ComplianceCard investor={investor} onChange={onComplianceChange} />
    </>
  );
}

// The compliance block: lifecycle stage, KYC, risk profile. Normally set by
// the account-opening flow; editable here so the back office can correct or
// re-assess without re-running onboarding.
function ComplianceCard({
  investor,
  onChange,
}: {
  investor: Investor;
  onChange: (investor: Investor) => void;
}) {
  const [stage, setStage] = useState<InvestorStage>(investor.stage);
  const [kyc, setKyc] = useState<KycStatus>(investor.kyc_status);
  const [risk, setRisk] = useState(
    investor.risk_profile != null ? String(investor.risk_profile) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    stage !== investor.stage ||
    kyc !== investor.kyc_status ||
    risk !== (investor.risk_profile != null ? String(investor.risk_profile) : "");

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const { investor: updated } = await api.updateCompliance(investor.id, {
        stage,
        kyc_status: kyc,
        risk_profile: risk ? Number(risk) : null,
      });
      onChange(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="section-title">Compliance</h2>
      <div className="card-form">
        <div className="field-row">
          <label className="field field-grow">
            <span>Stage</span>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value as InvestorStage)}
            >
              <option value="prospect">Prospect</option>
              <option value="onboarding">Onboarding</option>
              <option value="active">Active</option>
            </select>
          </label>
          <label className="field field-grow">
            <span>KYC status</span>
            <select value={kyc} onChange={(e) => setKyc(e.target.value as KycStatus)}>
              <option value="none">Not started</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </label>
          <label className="field field-grow">
            <span>Risk profile</span>
            <select value={risk} onChange={(e) => setRisk(e.target.value)}>
              <option value="">Not assessed</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n} — {RISK_LABELS[n]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {investor.kyc_reviewed_at && (
          <p className="muted">
            KYC last reviewed {formatDateTime(investor.kyc_reviewed_at)}.
          </p>
        )}
        {error && <div className="banner-error">{error}</div>}
        {dirty && (
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? "Saving…" : "Save compliance"}
            </button>
          </div>
        )}
        {saved && !dirty && <p className="muted">Saved.</p>}
      </div>
    </>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value || "—"}</dd>
    </div>
  );
}

function formatCity(zip: string | null, city: string | null): string | null {
  const z = zip?.trim();
  const c = city?.trim();
  if (z && c) return `${z} ${c}`;
  return c || z || null;
}
